
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import { HappyUsers } from '@/components/landing/HappyUsers';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import ByteDanceSvg from '~/svg/bytedance.svg';
const SHOWCASE_CDN = 'https://media.easy-peasy.ai/landing/seedream-5-pro';
const GENERATOR_URL = '/ai-images?model=Seedream%205.0%20Pro';
const EDITOR_URL = '/ai-images?action=edit-image&editModel=Seedream%205.0%20Pro';
type ShowcaseImage = {
    src: string;
    width: number;
    height: number;
    title: string;
    description: string;
};
/**
 * Seedream 5.0 Pro outputs generated through Easy-Peasy's image API. The
 * selected 1K results were re-encoded as WebP and stored in the R2 `images`
 * bucket under `landing/seedream-5-pro/generated/`.
 */
const SHOWCASE_IMAGES: ShowcaseImage[] = [
    {
        src: `${SHOWCASE_CDN}/generated/seed-infographic.webp`,
        width: 1024,
        height: 1024,
        title: 'English Educational Infographics',
        description: 'A complete four-stage botanical explainer with accurate, readable English labels.',
    },
    {
        src: `${SHOWCASE_CDN}/generated/lumen-interface.webp`,
        width: 1024,
        height: 1024,
        title: 'Product Interface Concepts',
        description: 'A calm creative-planning workspace with a coherent grid and concise English navigation.',
    },
    {
        src: `${SHOWCASE_CDN}/generated/botanical-product.webp`,
        width: 1024,
        height: 1024,
        title: 'Luxury Product Photography',
        description: 'Controlled glass, liquid, stone, and flower details composed like a finished fragrance campaign.',
    },
    {
        src: `${SHOWCASE_CDN}/generated/glass-whale.webp`,
        width: 1024,
        height: 1024,
        title: 'Cinematic Concept Art',
        description: 'A translucent whale, mirrored salt flat, and tiny human figure arranged with cinematic scale.',
    },
    {
        src: `${SHOWCASE_CDN}/generated/wonder-poster.webp`,
        width: 1024,
        height: 1024,
        title: 'Readable English Typography',
        description: 'A polished museum poster that renders “MAKE ROOM FOR WONDER” exactly as prompted.',
    },
    {
        src: `${SHOWCASE_CDN}/generated/ceramic-artist.webp`,
        width: 1024,
        height: 1024,
        title: 'Natural Editorial Photography',
        description: 'Warm window light, natural skin, linen, clay, and studio detail captured as an editorial moment.',
    },
    {
        src: `${SHOWCASE_CDN}/generated/japandi-interior.webp`,
        width: 1024,
        height: 1024,
        title: 'Interior Photography',
        description: 'Soft architectural light, tactile materials, and balanced styling in a warm Japandi living room.',
    },
    {
        src: `${SHOWCASE_CDN}/generated/food-editorial.webp`,
        width: 1024,
        height: 1024,
        title: 'Food Editorial',
        description: 'A fine-dining pear dessert with convincing steam, spun sugar, glaze, and dramatic side light.',
    },
];
const CAPABILITIES = [
    {
        number: '01',
        title: 'Dense visuals that still read clearly',
        description: 'Turn a long brief into an infographic, interface, storyboard, or campaign layout with hierarchy intact. Seedream reasons about what belongs together before it renders the page.',
    },
    {
        number: '02',
        title: 'Edits that follow the whole brief',
        description: 'Upload up to 10 reference images, describe what should change, and preserve the context that should stay. Use annotated references when placement matters.',
    },
    {
        number: '03',
        title: 'Photography with believable light',
        description: 'Create portraits, interiors, products, and everyday scenes with more convincing skin, material texture, shadows, and environmental light.',
    },
    {
        number: '04',
        title: 'Multilingual design without a workaround',
        description: 'Prompt and render text across a dozen widely used languages, making localized posters, menus, signs, and educational graphics a first-class workflow.',
    },
];
const WORKFLOW_STEPS = [
    {
        step: '1',
        title: 'Choose Seedream 5.0 Pro',
        description: 'Open the Image Generator and select Seedream 5.0 Pro from the model picker.',
    },
    {
        step: '2',
        title: 'Write the brief or add references',
        description: 'Start from text, or upload up to 10 images for editing, style, subject, and layout guidance.',
    },
    {
        step: '3',
        title: 'Pick 1K or 2K and create',
        description: 'Choose the aspect ratio and resolution that match the job, then generate and refine from the result.',
    },
];
const FAQS = [
    {
        question: 'What is Seedream 5.0 Pro?',
        answer: "Seedream 5.0 Pro is ByteDance's flagship multimodal image-generation model. It is designed for advanced reasoning, high-density visual communication, precise image editing, photographic realism, and native multilingual generation.",
    },
    {
        question: 'What does unrestricted mean on this page?',
        answer: 'Unrestricted describes the broad creative workflow available through Easy-Peasy, including image generation and editing with real-person reference photos. Requests must still follow our Terms of Service and applicable law.',
    },
    {
        question: 'Can I edit existing images with Seedream 5.0 Pro?',
        answer: 'Yes. Switch to image editing, upload one or more references, and describe the change you want. Easy-Peasy supports up to 10 reference images in a Seedream 5.0 Pro edit.',
    },
    {
        question: 'Which resolutions and aspect ratios are available?',
        answer: 'You can generate at 1K or 2K in square, portrait, vertical, landscape, and widescreen formats: 1:1, 3:4, 9:16, 4:3, and 16:9. Editing can also match the source image automatically.',
    },
    {
        question: 'Does Seedream 5.0 Pro generate readable text?',
        answer: 'It is built for text-rich visuals and natively supports a dozen widely used languages. As with every image model, review critical names, prices, instructions, and legal copy before publishing.',
    },
    {
        question: 'How much does Seedream 5.0 Pro cost on Easy-Peasy?',
        answer: 'A 1K image uses 4 credits and a 2K image uses 8 credits. The same resolution-based pricing applies to generation and editing.',
    },
    {
        question: 'How is Seedream 5.0 Pro different from Seedream 5.0 Lite?',
        answer: 'Choose Pro for complex prompts, dense layouts, stronger realism, and deliberate production work. Choose Lite when speed and lower cost matter more; Lite uses 2 credits and can generate up to 3K.',
    },
    {
        question: 'How were the examples on this page created?',
        answer: 'Every showcase image was generated with Seedream 5.0 Pro through the Easy-Peasy image API. We used English-first prompts, reviewed the results, and selected the eight outputs shown here.',
    },
];
const CheckIcon = () => (<svg className='h-4 w-4 flex-none' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
    <path d='m4 10.5 3.6 3.6L16 5.7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
  </svg>);
const ArrowIcon = () => (<svg className='h-5 w-5 transition-transform duration-200 group-hover:translate-x-1' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
    <path d='M3 10h13m-5-5 5 5-5 5' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'/>
  </svg>);
export default function Seedream50ProLandingPage() {
    const { t } = useLocale('seedream-5-pro');
    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((faq) => ({
            '@type': 'Question',
            name: t(faq.question),
            acceptedAnswer: {
                '@type': 'Answer',
                text: t(faq.answer),
            },
        })),
    };
    return (<>
      <Seo templateTitle={t('Unrestricted Seedream 5.0 Pro AI Image Generator')} description={t('Create and edit high-density images with ByteDance Seedream 5.0 Pro on Easy-Peasy.AI. Use up to 10 references, multilingual text, and 1K or 2K output.')} image={`${SHOWCASE_CDN}/generated/wonder-poster.webp`} page='product' productName='Seedream 5.0 Pro AI Image Generator' productDescription={t('Generate and edit production-ready images with ByteDance Seedream 5.0 Pro on Easy-Peasy.AI.')} productImage={[
            `${SHOWCASE_CDN}/generated/wonder-poster.webp`,
            `${SHOWCASE_CDN}/generated/ceramic-artist.webp`,
            `${SHOWCASE_CDN}/generated/glass-whale.webp`,
        ]}/>
      <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}/>

      <main className='overflow-hidden bg-[#f6f1e7] text-[#17213a]'>
        {/* Hero */}
        <section className='relative isolate min-h-screen overflow-hidden'>
          <div className='absolute inset-x-0 top-0 z-30'>
            <LandingPageNavigation />
          </div>

          <div className='pointer-events-none absolute inset-0 opacity-60' aria-hidden='true' style={{
            backgroundImage: 'linear-gradient(rgba(23,33,58,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(23,33,58,0.045) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
        }}/>
          <div className='pointer-events-none absolute -right-40 top-16 h-[34rem] w-[34rem] rounded-full bg-[#d8cdfd]/50 blur-3xl' aria-hidden='true'/>

          <div className='relative mx-auto grid min-h-screen max-w-7xl items-center gap-14 px-6 pb-20 pt-36 lg:grid-cols-[0.88fr_1.12fr] lg:px-8 lg:pb-24 lg:pt-40'>
            <div className='relative z-10 max-w-2xl'>
              <div className='mb-8 inline-flex items-center gap-3 rounded-full border border-[#17213a]/10 bg-white/80 py-2 pl-2 pr-4 text-sm font-semibold text-[#39445d] shadow-sm'>
                <span className='flex h-8 w-8 items-center justify-center rounded-full bg-white ring-1 ring-[#17213a]/10'>
                  <ByteDanceSvg className='h-5 w-5' aria-hidden='true'/>
                </span>
                {t("ByteDance's flagship image model")}
              </div>

              <div className='mb-5 flex items-center gap-3 text-sm font-black uppercase tracking-[0.22em] text-[#5b43d6]'>
                <span className='h-px w-12 bg-[#5b43d6]' aria-hidden='true'/>
                {t('Unrestricted image creation')}
              </div>

              <h1 className='max-w-[9ch] text-[clamp(4rem,8vw,7.5rem)] font-black leading-[0.82] tracking-[-0.065em] text-[#17213a]'>
                <span className='block'>Seedream</span>
                <span className='mt-3 block text-[#5b43d6]'>5.0 Pro</span>
              </h1>

              <p className='mt-8 max-w-xl text-xl font-semibold leading-8 text-[#25304b] sm:text-2xl'>
                {t('Think through the brief. Build the whole visual.')}
              </p>
              <p className='mt-4 max-w-xl text-base leading-7 text-[#5a6275] sm:text-lg'>
                {t('Generate polished images from detailed prompts, or edit with up to 10 references. Seedream 5.0 Pro handles dense layouts, multilingual text, realistic photography, and 2K delivery in one model.')}
              </p>

              <div className='mt-9 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center'>
                <Link href={GENERATOR_URL} className='group inline-flex min-h-[52px] items-center justify-center gap-3 rounded-full bg-[#17213a] px-7 py-3 text-base font-bold text-[#f8f4ea] shadow-[0_8px_0_#5b43d6] transition-transform duration-200 hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#5b43d6]'>
                  {t('Create with Seedream 5.0 Pro')}
                  <ArrowIcon />
                </Link>
                <Link href={EDITOR_URL} className='group inline-flex min-h-[52px] items-center justify-center gap-3 rounded-full border-2 border-[#17213a] bg-white/60 px-7 py-3 text-base font-bold text-[#17213a] transition-colors duration-200 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#5b43d6]'>
                  {t('Edit an image with Seedream')}
                  <ArrowIcon />
                </Link>
              </div>

              <div className='mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm font-medium text-[#5a6275]'>
                {[
            'Text to image',
            'Up to 10 references',
            '1K or 2K output',
        ].map((item) => (<span key={item} className='inline-flex items-center gap-2'>
                    <span className='text-[#5b43d6]'>
                      <CheckIcon />
                    </span>
                    {t(item)}
                  </span>))}
              </div>
            </div>

            <div className='relative mx-auto min-h-[480px] w-full max-w-2xl sm:min-h-[590px] lg:min-h-[690px]'>
              <figure className='absolute left-0 top-[4%] w-[78%] overflow-hidden rounded-[2rem] border-[8px] border-white bg-white shadow-[0_28px_70px_rgba(23,33,58,0.2)]'>
                <div className='relative aspect-[16/10]'>
                  <Image src={`${SHOWCASE_CDN}/generated/ceramic-artist.webp`} alt={t('Seedream 5.0 Pro editorial photograph of a ceramic artist working in warm window light')} fill priority sizes='(max-width: 1024px) 75vw, 42vw' className='object-cover'/>
                </div>
              </figure>

              <figure className='absolute right-0 top-[20%] w-[38%] rotate-[2deg] overflow-hidden rounded-[1.6rem] border-[7px] border-white bg-white shadow-[0_22px_60px_rgba(23,33,58,0.2)]'>
                <div className='relative aspect-square'>
                  <Image src={`${SHOWCASE_CDN}/generated/wonder-poster.webp`} alt={t('English museum poster generated with Seedream 5.0 Pro')} fill sizes='(max-width: 1024px) 38vw, 20vw' className='object-cover'/>
                </div>
              </figure>

              <figure className='absolute bottom-[3%] left-[10%] w-[70%] -rotate-[1.5deg] overflow-hidden rounded-[1.8rem] border-[8px] border-white bg-white shadow-[0_24px_70px_rgba(23,33,58,0.22)]'>
                <div className='relative aspect-video'>
                  <Image src={`${SHOWCASE_CDN}/generated/glass-whale.webp`} alt={t('Cinematic glass whale concept generated with Seedream 5.0 Pro')} fill sizes='(max-width: 1024px) 70vw, 38vw' className='object-cover'/>
                </div>
              </figure>

              <div className='absolute bottom-[1%] right-[2%] rounded-full bg-[#f3b736] px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-[#17213a] shadow-lg'>
                {t('Made in Easy-Peasy')}
              </div>
            </div>
          </div>
        </section>

        {/* Quick facts */}
        <section className='border-y border-[#17213a]/10 bg-white/70'>
          <div className='mx-auto grid max-w-7xl grid-cols-2 divide-x divide-y divide-[#17213a]/10 px-6 sm:grid-cols-4 sm:divide-y-0 lg:px-8'>
            {[
            ['10', 'reference images'],
            ['2K', 'maximum output'],
            ['12+', 'generation languages'],
            ['4', 'credits from'],
        ].map(([value, label]) => (<div key={label} className='px-4 py-8 text-center sm:py-10'>
                <div className='text-3xl font-black tracking-tight text-[#17213a] sm:text-4xl'>
                  {value}
                </div>
                <div className='mt-1 text-xs font-bold uppercase tracking-[0.14em] text-[#6a7182]'>
                  {t(label)}
                </div>
              </div>))}
          </div>
        </section>

        {/* Easy-Peasy-generated showcase */}
        <section id='examples' className='bg-[#17213a] py-24 sm:py-32'>
          <div className='mx-auto max-w-7xl px-6 lg:px-8'>
            <div className='flex flex-col gap-8 border-b border-[#f6f1e7]/15 pb-12 lg:flex-row lg:items-end lg:justify-between'>
              <div className='max-w-3xl'>
                <p className='text-xs font-black uppercase tracking-[0.22em] text-[#b7a8ff]'>
                  {t('English-first showcase')}
                </p>
                <h2 className='mt-5 text-4xl font-black tracking-[-0.04em] text-[#f6f1e7] sm:text-6xl'>
                  {t('What Seedream 5.0 Pro can hold in one frame')}
                </h2>
              </div>
              <Link href={EDITOR_URL} className='group inline-flex items-center gap-2 text-sm font-bold text-[#f3b736] underline decoration-[#f3b736]/40 decoration-2 underline-offset-8 hover:decoration-[#f3b736]'>
                {t('Edit with Seedream 5.0 Pro')}
                <ArrowIcon />
              </Link>
            </div>

            <div className='mt-12 columns-1 gap-5 md:columns-2 lg:columns-3'>
              {SHOWCASE_IMAGES.map((item) => (<figure key={item.src} className='mb-5 break-inside-avoid overflow-hidden rounded-2xl bg-[#222e4b]'>
                  <Image src={item.src} alt={t(item.description)} width={item.width} height={item.height} sizes='(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw' className='h-auto w-full'/>
                  <figcaption className='border-t border-[#f6f1e7]/10 px-5 py-5'>
                    <div className='text-base font-bold text-[#f6f1e7]'>
                      {t(item.title)}
                    </div>
                    <p className='mt-2 text-sm leading-6 text-[#c5c9d3]'>
                      {t(item.description)}
                    </p>
                  </figcaption>
                </figure>))}
            </div>

            <p className='mt-8 text-sm leading-6 text-[#9198aa]'>
              {t('Every image above was generated through the Easy-Peasy API with Seedream 5.0 Pro, then reviewed for visual quality and accurate English text.')}
            </p>
          </div>
        </section>

        {/* Capabilities */}
        <section className='bg-[#f6f1e7] py-24 sm:py-32'>
          <div className='mx-auto max-w-7xl px-6 lg:px-8'>
            <div className='grid gap-12 lg:grid-cols-[0.72fr_1.28fr] lg:gap-20'>
              <div>
                <p className='text-xs font-black uppercase tracking-[0.22em] text-[#5b43d6]'>
                  {t('Why Pro')}
                </p>
                <h2 className='mt-5 max-w-md text-4xl font-black tracking-[-0.04em] text-[#17213a] sm:text-6xl'>
                  {t('It reasons like a designer before it draws')}
                </h2>
                <p className='mt-6 max-w-md text-lg leading-8 text-[#626a7c]'>
                  {t('The upgrade is not just more detail. It is better visual planning: understanding the brief, organizing the information, and preserving intent through generation or editing.')}
                </p>
              </div>

              <div className='border-t-2 border-[#17213a]'>
                {CAPABILITIES.map((capability) => (<div key={capability.number} className='grid gap-4 border-b border-[#17213a]/20 py-8 sm:grid-cols-[4rem_1fr] sm:gap-8'>
                    <span className='text-sm font-black tracking-[0.16em] text-[#5b43d6]'>
                      {capability.number}
                    </span>
                    <div>
                      <h3 className='text-2xl font-black tracking-[-0.025em] text-[#17213a]'>
                        {t(capability.title)}
                      </h3>
                      <p className='mt-3 max-w-2xl text-base leading-7 text-[#626a7c]'>
                        {t(capability.description)}
                      </p>
                    </div>
                  </div>))}
              </div>
            </div>
          </div>
        </section>

        {/* Workflow */}
        <section className='bg-[#5b43d6] py-24 text-[#fbf8ef] sm:py-32'>
          <div className='mx-auto max-w-7xl px-6 lg:px-8'>
            <div className='max-w-3xl'>
              <p className='text-xs font-black uppercase tracking-[0.22em] text-[#e4dcff]'>
                {t('From brief to image')}
              </p>
              <h2 className='mt-5 text-4xl font-black tracking-[-0.04em] sm:text-6xl'>
                {t('Three steps. No prompt engineering course.')}
              </h2>
            </div>

            <div className='mt-16 grid border-y border-[#fbf8ef]/35 lg:grid-cols-3 lg:divide-x lg:divide-[#fbf8ef]/35'>
              {WORKFLOW_STEPS.map((item) => (<div key={item.step} className='border-b border-[#fbf8ef]/35 py-9 last:border-b-0 lg:border-b-0 lg:px-9 lg:first:pl-0 lg:last:pr-0'>
                  <div className='flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#fbf8ef] text-lg font-black'>
                    {item.step}
                  </div>
                  <h3 className='mt-8 text-2xl font-black'>{t(item.title)}</h3>
                  <p className='mt-3 max-w-sm text-base leading-7 text-[#e4dcff]'>
                    {t(item.description)}
                  </p>
                </div>))}
            </div>

            <Link href={GENERATOR_URL} className='group mt-10 inline-flex min-h-[52px] items-center justify-center gap-3 rounded-full bg-[#f3b736] px-7 py-3 font-black text-[#17213a] transition-transform duration-200 hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#fbf8ef]'>
              {t('Open the Image Generator')}
              <ArrowIcon />
            </Link>
          </div>
        </section>

        {/* Specifications and comparison */}
        <section className='bg-white py-24 sm:py-32'>
          <div className='mx-auto max-w-7xl px-6 lg:px-8'>
            <div className='grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:items-start lg:gap-20'>
              <div>
                <p className='text-xs font-black uppercase tracking-[0.22em] text-[#5b43d6]'>
                  {t('Model guide')}
                </p>
                <h2 className='mt-5 text-4xl font-black tracking-[-0.04em] text-[#17213a] sm:text-6xl'>
                  {t('Pro when the image has a job to do')}
                </h2>
                <p className='mt-6 max-w-lg text-lg leading-8 text-[#626a7c]'>
                  {t('Choose Pro when layout, realism, readable text, or reference control matters more than the quickest draft.')}
                </p>

                <dl className='mt-10 border-t border-[#17213a]/15'>
                  {[
            ['Modes', 'Text to image and image editing'],
            ['References', 'Up to 10 images per edit'],
            ['Output', '1K or 2K'],
            ['Aspect ratios', '1:1, 3:4, 4:3, 9:16, 16:9'],
            ['Generation cost', '4 credits at 1K · 8 credits at 2K'],
        ].map(([term, detail]) => (<div key={term} className='grid grid-cols-[7.5rem_1fr] gap-4 border-b border-[#17213a]/15 py-5 text-sm sm:grid-cols-[10rem_1fr]'>
                      <dt className='font-black text-[#17213a]'>{t(term)}</dt>
                      <dd className='text-[#626a7c]'>{t(detail)}</dd>
                    </div>))}
                </dl>
              </div>

              <div className='overflow-x-auto rounded-[1.75rem] bg-[#f6f1e7] p-2 sm:p-5'>
                <table className='min-w-[620px] w-full border-collapse text-left'>
                  <thead>
                    <tr>
                      <th className='px-5 py-5 text-xs font-black uppercase tracking-[0.14em] text-[#6a7182]'>
                        {t('Compare')}
                      </th>
                      <th className='bg-[#17213a] px-5 py-5 text-base font-black text-[#f6f1e7]'>
                        Seedream 5.0 Pro
                      </th>
                      <th className='px-5 py-5 text-base font-black text-[#17213a]'>
                        Seedream 5.0 Lite
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
            ['Best for', 'Production work', 'Fast drafts'],
            ['Reasoning', 'Deep, layout-aware', 'Fast, intent-aware'],
            ['Resolution', '1K or 2K', '1K, 2K, or 3K'],
            ['Cost', '4 or 8 credits', '2 credits'],
        ].map(([label, pro, lite]) => (<tr key={label} className='border-t border-[#17213a]/10'>
                        <th className='px-5 py-5 text-sm font-black text-[#17213a]'>
                          {t(label)}
                        </th>
                        <td className='bg-[#17213a] px-5 py-5 text-sm font-semibold text-[#f6f1e7]'>
                          {t(pro)}
                        </td>
                        <td className='px-5 py-5 text-sm text-[#626a7c]'>
                          {t(lite)}
                        </td>
                      </tr>))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className='bg-[#f6f1e7] py-24 sm:py-32'>
          <div className='mx-auto max-w-4xl px-6 lg:px-8'>
            <div className='text-center'>
              <p className='text-xs font-black uppercase tracking-[0.22em] text-[#5b43d6]'>
                {t('FAQ')}
              </p>
              <h2 className='mt-5 text-4xl font-black tracking-[-0.04em] text-[#17213a] sm:text-6xl'>
                {t('Seedream 5.0 Pro, explained')}
              </h2>
            </div>

            <div className='mt-14 border-t-2 border-[#17213a]'>
              {FAQS.map((faq) => (<details key={faq.question} className='group border-b border-[#17213a]/20'>
                  <summary className='flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-left text-lg font-black text-[#17213a] marker:content-none sm:text-xl'>
                    {t(faq.question)}
                    <span className='flex h-8 w-8 flex-none items-center justify-center rounded-full border border-[#17213a]/30 text-xl transition-transform duration-200 group-open:rotate-45'>
                      +
                    </span>
                  </summary>
                  <p className='max-w-3xl pb-7 pr-12 text-base leading-7 text-[#626a7c]'>
                    {t(faq.answer)}
                  </p>
                </details>))}
            </div>
          </div>
        </section>

        <div className='bg-white dark:bg-slate-900'>
          <FeaturedLogos />
          <HappyUsers />
          <Testimonials />
          <LogosCloud />
          <CallToAction preset='ai-images' href={GENERATOR_URL}/>
          <Footer />
        </div>
      </main>
    </>);
}
const getStaticProps: GetStaticProps = async () => ({
    props: {},
});

    async function __Next_Translate__getStaticProps__1a0cbfb65d8__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-image-generator/seedream-5-0-pro',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0cbfb65d8__ as getStaticProps }
  